import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { resizeImageIfNeeded } from "@/lib/imageResize";import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtKm, fmtCAD, todayISO } from "@/lib/format";
import { shareTripCalendar, emailTripInvite } from "@/lib/calendar";
import {
  getFamilyMembers, parseNotesMember, formatNotesWithMember, subscribeToFamilyChanges, addFamilyMember,
} from "@/lib/familyMembers";
import {
  Plus, ArrowsLeftRight, ArrowRight, Car, Receipt, Pill, Stethoscope,
  UploadSimple, FileImage, X, Sparkle, MagicWand, Camera, Envelope, UsersThree
} from "@phosphor-icons/react";

const DEFAULTS = {
  date: "",
  appointment_time: "",
  destination: "",
  destination_address: "",  // captured from letter for .ics location field
  provider: "",
  purpose: "",
  one_way_km: "",
  round_trip: true,
  parking: "", tolls: "", meals: "", accommodation: "",
  gas: "", taxi: "", rental: "",
  other_amount: "", other_desc: "",
  // CRA flat-rate meal claim (RC4065 simplified method):
  // "detailed" = user types a $ amount in the Meals field with an optional
  //   receipt photo (existing flow, default).
  // "flat_rate" = user claims 1, 2, or 3 meals × $23 with NO receipt
  //   required. CRA expects destination + purpose to be on record.
  meal_method: "detailed",
  meal_flat_rate_count: 0,
  category: "Travel",
  companion_required: false,
  companion_cert_date: "",
  companion_cert_doctor: "",
  service_kind: "prescription",
  drug_name: "", drug_pharmacy: "", din: "",
  service_total: "", service_covered: "", drug_amount: "",
  notes: "",
  receipt_file_ids: [],
  _receipt_meta: [],
  appointment_letter_file_ids: [],
  _letter_meta: [],
};

// CRA expense category options used in the Settings dropdowns and PDF export
export const CRA_CATEGORIES = [
  { v: "Travel",         label: "Travel" },
  { v: "Prescription",   label: "Prescription / drug" },
  { v: "Service",        label: "Service (physio, chiro, massage, exam)" },
  { v: "Dental",         label: "Dental" },
  { v: "Device",         label: "Medical device / equipment" },
  { v: "Premium",        label: "Private health insurance premium" },
  { v: "Attendant Care", label: "Attendant care / nursing home" },
  { v: "Other",          label: "Other eligible expense" },
];

// CRA simplified mileage rates ($/km) — kept in sync with backend PROVINCE_RATES
// Source: CRA "Meal and vehicle rates used to calculate travel expenses" — 2025
const CRA_MILEAGE_RATES = {
  AB: 0.55, BC: 0.57, MB: 0.56, NB: 0.585, NL: 0.605, NS: 0.575,
  NT: 0.725, NU: 0.71, ON: 0.62, PE: 0.575, QC: 0.59, SK: 0.535, YT: 0.705,
};

function n(x) { return Number(x) || 0; }

export default function TripFormDialog({ initial, trigger, open: openProp, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);
  // Support both controlled (open/onOpenChange props) and uncontrolled use.
  // Controlled mode is used by <LogTripButton> which opens this dialog
  // programmatically AFTER the user picks "Add to today's trip" vs "Start
  // a separate trip" in the smart chooser.
  const open = openProp !== undefined ? openProp : internalOpen;
  const setOpen = (v) => {
    if (openProp === undefined) setInternalOpen(v);
    onOpenChange?.(v);
  };
  const [showRx, setShowRx] = useState(false);
  // Unified document scanner — keep the explicit Receipt/Letter dropzones
  // hidden by default behind an "Advanced upload" toggle so the form's
  // primary upload UX is a single confident "Snap a document" CTA.
  const [showAdvancedUpload, setShowAdvancedUpload] = useState(false);
  const [syncCalendar, setSyncCalendar] = useState(true);  // .ics download toggle (default ON)
  // Set when an OCR scan suggests a co-pay was likely present but we
  // couldn't extract the amount — surfaces a visible banner above the
  // Out-of-pocket field so the user doesn't silently lose the deduction.
  const [oopUncertain, setOopUncertain] = useState(false);
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const letterFileRef = useRef(null);
  const receiptZoneRef = useRef(null);

  // Family Mode (MVP) — the chosen family member is encoded as a `[Name]`
  // prefix on the trip's notes field (see /lib/familyMembers.js). Parse
  // it out on load so the dropdown shows the right member and the
  // textarea shows only the user's prose.
  const _parsedNotes = parseNotesMember(initial?.notes || "");

  const [form, setForm] = useState({
    ...DEFAULTS,
    member: _parsedNotes.member,
    date: initial?.date || todayISO(),
    destination: initial?.destination || "",
    provider: initial?.provider || "",
    purpose: initial?.purpose || "",
    one_way_km: initial?.one_way_km ?? "",
    round_trip: initial?.round_trip ?? true,
    parking: initial?.parking || "",
    tolls: initial?.tolls || "",
    meals: initial?.meals || "",
    accommodation: initial?.accommodation || "",
    gas: initial?.gas || "",
    taxi: initial?.taxi || "",
    rental: initial?.rental || "",
    other_amount: initial?.other_amount || "",
    other_desc: initial?.other_desc || "",
    meal_method: initial?.meal_method || "detailed",
    meal_flat_rate_count: initial?.meal_flat_rate_count || 0,
    category: initial?.category || "Travel",
    companion_required: !!initial?.companion_required,
    companion_cert_date: initial?.companion_cert_date || "",
    companion_cert_doctor: initial?.companion_cert_doctor || "",
    service_kind: initial?.service_kind || "prescription",
    drug_name: initial?.drug_name || "",
    drug_pharmacy: initial?.drug_pharmacy || "",
    din: initial?.din || "",
    service_total: initial?.service_total || "",
    service_covered: initial?.service_covered || "",
    drug_amount: initial?.drug_amount || "",
    notes: _parsedNotes.cleanNotes,
    receipt_file_ids: initial?.receipt_file_ids || [],
    _receipt_meta: (initial?.receipt_file_ids || []).map((id) => ({ file_id: id, original_filename: "Receipt" })),
    appointment_letter_file_ids: initial?.appointment_letter_file_ids || [],
    _letter_meta: (initial?.appointment_letter_file_ids || []).map((id) => ({ file_id: id, original_filename: "Letter" })),
  });

  // Family Mode (MVP): keep the dropdown options in sync with the Settings
  // page. localStorage changes in another tab also propagate via the
  // subscribeToFamilyChanges helper.
  const [familyMembersList, setFamilyMembersList] = useState(getFamilyMembers());
  useEffect(() => subscribeToFamilyChanges(setFamilyMembersList), []);
  // If the user removed the member this trip was tagged to (in Settings),
  // we DON'T wipe the tag — that would silently lose the caregiver's
  // bookkeeping. Instead we surface the orphaned name in the dropdown
  // so the user can see what the trip was tagged as and choose to
  // re-add the member or reassign.
  const memberOptions = (() => {
    const list = [...familyMembersList];
    const current = (form.member || "").trim();
    if (current && !list.some((m) => m.toLowerCase() === current.toLowerCase())) {
      list.push(current);
    }
    return list;
  })();

  // Family Mode (MVP) — Option B: inline "+ Add family member..." flow
  // inside the Whose-appointment dropdown. Lets the user discover and
  // add members AT THE MOMENT they need to tag a trip, without ever
  // leaving the form. Saved name pushes into localStorage via the
  // shared addFamilyMember helper (so it also appears in the Settings
  // list + on other tabs).
  const ADD_MEMBER_SENTINEL = "__add_member__";
  const [addMemberMode, setAddMemberMode] = useState(false);
  const [newMemberDraft, setNewMemberDraft] = useState("");

  const handleMemberDropdownChange = (v) => {
    if (v === ADD_MEMBER_SENTINEL) {
      setAddMemberMode(true);
      return;
    }
    setForm({ ...form, member: v });
  };

  const commitNewMember = () => {
    const name = newMemberDraft.trim();
    if (!name) return;
    if (name.length > 40) {
      toast.error("Names must be 40 characters or less.");
      return;
    }
    // De-dupe case-insensitively against the existing list. If the user
    // typed a name that already exists, just select it instead of
    // creating a duplicate.
    const existing = memberOptions.find(
      (m) => m.toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      setForm({ ...form, member: existing });
      setAddMemberMode(false);
      setNewMemberDraft("");
      toast.info(`${existing} is already in your family list — selected it.`);
      return;
    }
    addFamilyMember(name); // pushes to localStorage + broadcasts change
    setForm({ ...form, member: name });
    setAddMemberMode(false);
    setNewMemberDraft("");
    toast.success(`Added ${name} — tagged this trip to them.`);
  };

  const cancelAddMember = () => {
    setAddMemberMode(false);
    setNewMemberDraft("");
  };

  const oneWay = n(form.one_way_km);
  const totalKm = form.round_trip ? oneWay * 2 : oneWay;
  const expenses = n(form.parking) + n(form.tolls) + n(form.meals) + n(form.accommodation)
                 + n(form.gas) + n(form.taxi) + n(form.rental) + n(form.other_amount);
  const stTotal = n(form.service_total);
  const stCovered = n(form.service_covered);
  // Out-of-pocket: PREFER the user's explicit `drug_amount` value (which
  // OCR also writes into when it extracts an OOP) so the user can
  // override the computed value at any time. Fall back to total-minus-
  // covered when the user hasn't typed an OOP and a total is present.
  const outOfPocket = n(form.drug_amount) > 0
    ? n(form.drug_amount)
    : (stTotal > 0 ? Math.max(0, stTotal - stCovered) : 0);

  // Track which money fields were filled by OCR (vs. typed by the user).
  // Drives the "Report wrong amount" chip — only shown beside fields the
  // OCR populated, so we don't badger users about values they typed.
  const [ocrFields, setOcrFields] = useState({});
  // Last OCR scan context — captured when applyScan/applyLetterScan run
  // so the feedback chip knows which file & vendor to attribute to.
  const [lastScan, setLastScan] = useState(null);

  // Co-pay uncertainty — surface a banner above the Out-of-pocket field
  // whenever the form looks like a fully-insured statement (Total ≈ Covered,
  // OOP = 0). This catches the common dental-statement layout where the
  // scanner reads "Total" and "Ins Estimate" matching exactly, but the
  // patient actually paid a small co-pay by card that the scan missed.
  // Combines the backend's explicit flag (oopUncertain state) with a
  // defensive client-side computation so the user always gets a heads-up.
  const oopLooksZeroSuspicious =
    stTotal > 0 &&
    Math.abs(stTotal - stCovered) < 0.01 &&
    outOfPocket === 0;
  const showOopUncertain = oopUncertain || oopLooksZeroSuspicious;

  // Live CRA mileage math — uses the user's province (or custom override) to
  // show the eligible dollar value the moment they type a distance.
  const { user } = useAuth();
  const mileageRate = user?.custom_rate || CRA_MILEAGE_RATES[user?.province] || CRA_MILEAGE_RATES.ON;
  const mileageDollars = totalKm * mileageRate;
  const craTier = oneWay >= 80 ? "80+" : oneWay >= 40 ? "40-79" : "<40";
  // CRA only allows mileage credit when one-way distance >= 40km
  const eligibleMileage = oneWay >= 40 ? mileageDollars : 0;
  const tripTotal = expenses + outOfPocket + eligibleMileage;

  const applyScan = (s) => {
    if (!s) return;
    // Capture last-scan context for the "Report wrong amount" feedback chip.
    setLastScan({
      kind: "receipt",
      vendor: s.vendor || null,
      total: s.total ?? null,
      out_of_pocket: s.out_of_pocket ?? null,
      file_id: s._file_id || null,
    });
    const ocrFlags = {};
    const isEdit = !!initial?.id;
    const isBlank = (v) => v == null || v === "" || v === "0" || v === "0.00";
    const parseNum = (v) => {
      const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
      return Number.isFinite(n) ? n : 0;
    };
    // Numeric expense fields — multiple receipts in the same category should ADD up, not replace
    const NUMERIC_KEYS = new Set([
      "parking", "tolls", "meals", "gas", "taxi", "rental", "accommodation",
      "other_amount", "service_total", "service_covered", "drug_amount",
    ]);
    const addedTo = []; // for toast feedback
    const set = (next, key, value) => {
      if (NUMERIC_KEYS.has(key)) {
        const newAmt = parseNum(value);
        if (newAmt <= 0) return;
        const cur = parseNum(next[key]);
        const total = cur + newAmt;
        next[key] = total.toFixed(2);
        ocrFlags[key] = true;
        if (cur > 0) addedTo.push({ key, prev: cur, added: newAmt, total });
        return;
      }
      // Non-numeric fields: in edit mode keep existing values; otherwise fill if blank
      if (isEdit && !isBlank(next[key])) return;
      if (!isBlank(next[key]) && !isEdit) return; // also don't clobber user-typed text fields mid-flow
      next[key] = value;
    };
    setForm((f) => {
      const next = { ...f };
      if (s.date && isBlank(next.date)) next.date = s.date;
      // CRA category from scan
      if (s.cra_category && (!next.category || next.category === "Travel")) {
        next.category = s.cra_category;
      }
      // Travel expense
      if (s.kind === "travel_expense" && s.total != null) {
        const cat = s.category || "other";
        const amt = String(Number(s.total).toFixed(2));
        if (cat in next && cat !== "other" && NUMERIC_KEYS.has(cat)) {
          set(next, cat, amt);
        } else {
          set(next, "other_amount", amt);
          if (s.vendor) set(next, "other_desc", s.vendor);
        }
        if (s.vendor) set(next, "purpose", s.vendor);
      }
      // Prescription / service
      if (s.kind === "prescription" || s.kind === "service") {
        if (!isEdit) next.service_kind = s.kind;
        if (s.service_or_medication) set(next, "drug_name", s.service_or_medication);
        if (s.vendor) set(next, "drug_pharmacy", s.vendor);
        if (s.din) set(next, "din", s.din);
        if (s.doctor) set(next, "provider", s.doctor);
        if (s.total != null) set(next, "service_total", String(Number(s.total).toFixed(2)));
        if (s.covered_by_insurance != null) set(next, "service_covered", String(Number(s.covered_by_insurance).toFixed(2)));
        if (s.out_of_pocket != null) set(next, "drug_amount", String(Number(s.out_of_pocket).toFixed(2)));
      }
      return next;
    });
    // Mark fields that OCR populated so the UI can show "Report wrong" chips.
    setOcrFields((prev) => ({ ...prev, ...ocrFlags }));
    // CRA eligibility warning — surface as a high-visibility toast
    if (s.cra_eligibility === "ineligible") {
      toast.warning("This expense may not be CRA-eligible", {
        description: s.cra_eligibility_reason || "Common ineligible items: gym memberships, OTC vitamins, cosmetic procedures. Confirm with your accountant before claiming.",
        duration: 9000,
      });
    }
    if (addedTo.length > 0) {
      // Build a friendly toast: "+$3.50 added to Meals (was $5.00, now $8.50)"
      const item = addedTo[0];
      const label = item.key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
      toast.success(`+$${item.added.toFixed(2)} added to ${label}`, {
        description: `Was $${item.prev.toFixed(2)}, now $${item.total.toFixed(2)}. ${addedTo.length > 1 ? `+${addedTo.length - 1} more fields updated.` : ""}`,
      });
    } else {
      toast.success(isEdit
        ? "Receipt attached. Review and save."
        : "Receipt scanned — fields auto-filled");
    }
  };

  const upload = useMutation({
    mutationFn: async (rawFile) => {
      const file = await resizeImageIfNeeded(rawFile);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Try to scan it (best-effort)
      let scanData = null;
      try {
        const r = await api.post(`/receipts/scan?file_id=${data.file_id}`);
        scanData = { ...r.data, _file_id: data.file_id };
      } catch { /* scan failed — keep going */ }
      return { ...data, scan: scanData };
    },
    onSuccess: ({ file_id, original_filename, scan }) => {
      setForm((f) => ({
        ...f,
        receipt_file_ids: [...f.receipt_file_ids, file_id],
        _receipt_meta: [...f._receipt_meta, { file_id, original_filename }],
      }));
      toast.success("Receipt uploaded");
      if (scan) applyScan(scan);
    },
    onError: () => toast.error("Upload failed"),
  });

  const removeReceipt = (id) => setForm((f) => ({
    ...f,
    receipt_file_ids: f.receipt_file_ids.filter((x) => x !== id),
    _receipt_meta: f._receipt_meta.filter((x) => x.file_id !== id),
  }));

  // Appointment letter — separate scan endpoint that extracts doctor / clinic / date / purpose / CRA category.
  // After applying fields, if user has a home address on profile AND the letter
  // contained a clinic address, we automatically geocode the clinic and ask the
  // backend for driving distance — auto-populating one_way_km without the user
  // touching the address fields. This is the "Auto-Mileage from Letter" pipeline.
  const applyLetterScan = async (s) => {
    if (!s) return;
    setLastScan({
      kind: "letter",
      vendor: s.clinic_name || null,
      total: s.service_total ?? null,
      out_of_pocket: s.out_of_pocket ?? null,
      file_id: s._file_id || null,
    });
    const ocrFlags = {};
    const isEdit = !!initial?.id;
    const isBlank = (v) => v == null || v === "";
    setForm((f) => {
      const next = { ...f };
      if (s.date && (isBlank(next.date) || !isEdit)) next.date = s.date;
      if (s.appointment_time && isBlank(next.appointment_time)) next.appointment_time = s.appointment_time;
      if (s.clinic_name && isBlank(next.destination)) next.destination = s.clinic_name;
      if (s.clinic_address && isBlank(next.destination_address)) next.destination_address = s.clinic_address;
      if (s.doctor_name && isBlank(next.provider)) next.provider = s.doctor_name;
      if (s.purpose && isBlank(next.purpose)) next.purpose = s.purpose;
      if (s.cra_category && (!next.category || next.category === "Travel")) {
        next.category = s.cra_category;
      }
      // Financial fields — only when the scanned doc included $ amounts
      // (paid statements often combine appointment + payment info). Auto-
      // flip the Type dropdown from "prescription" to "service" so the
      // numbers land in the visible fields instead of staying hidden.
      const hasMoney = s.service_total != null || s.service_covered != null || s.out_of_pocket != null;
      if (hasMoney) {
        if (!isEdit) next.service_kind = "service";
        if (s.service_total != null && isBlank(next.service_total)) {
          next.service_total = String(Number(s.service_total).toFixed(2));
          ocrFlags.service_total = true;
        }
        if (s.service_covered != null && isBlank(next.service_covered)) {
          next.service_covered = String(Number(s.service_covered).toFixed(2));
          ocrFlags.service_covered = true;
        }
        if (s.out_of_pocket != null && isBlank(next.drug_amount)) {
          next.drug_amount = String(Number(s.out_of_pocket).toFixed(2));
          ocrFlags.drug_amount = true;
        }
        // Also mirror trip-level metadata into the Prescriptions/Services
        // section so the user sees the actual dental/physio service name
        // instead of staring at placeholder text. drug_name = the
        // procedure (purpose), drug_pharmacy = the clinic.
        if (s.purpose && isBlank(next.drug_name)) next.drug_name = s.purpose;
        if (s.clinic_name && isBlank(next.drug_pharmacy)) next.drug_pharmacy = s.clinic_name;
      }
      return next;
    });
    setOcrFields((prev) => ({ ...prev, ...ocrFlags }));
    const moneyMsg = (s.service_total != null || s.out_of_pocket != null)
      ? ` · expense fields filled (${s.out_of_pocket_derived ? "computed" : "detected"})`
      : "";
    toast.success(`Appointment letter scanned — fields auto-filled${moneyMsg}`);
    // Surface uncertainty: if model returned total == covered (suspicious
    // on a paid statement — usually means the header "Total" was actually
    // the insurance estimate and a real co-pay line was missed) prompt
    // the user to verify the out-of-pocket value manually.
    if (s.out_of_pocket_uncertain) {
      setOopUncertain(true);
      toast("Couldn't read the patient-paid amount — please double-check the Out-of-pocket field.", {
        duration: 8000,
        icon: "⚠️",
      });
    } else if (s.out_of_pocket != null && Number(s.out_of_pocket) > 0) {
      // OCR did find an OOP amount — clear any prior uncertainty.
      setOopUncertain(false);
    }

    // === Auto-mileage pipeline ===
    // Only runs for trip entries (not Rx). Needs both endpoints set.
    if (initial?.entry_type === "prescription") return;
    const homeLat = user?.home_lat;
    const homeLng = user?.home_lng;
    if (!homeLat || !homeLng) {
      // Surface this once so the user knows the auto-mileage was skipped
      toast("Set your home address in Settings to auto-calculate mileage from letters.", { duration: 4500 });
      return;
    }
    // Prefer clinic_address (most precise); fall back to clinic_name
    const destQuery = s.clinic_address || s.clinic_name;
    if (!destQuery) return;
    try {
      toast.loading("Calculating mileage from letter…", { id: "auto-mileage" });
      const { data: geo } = await api.get(`/geocode?q=${encodeURIComponent(destQuery)}`);
      const clinic = geo?.best;
      if (!clinic) throw new Error("clinic not found");
      const { data: dist } = await api.get(
        `/distance?from_lat=${homeLat}&from_lng=${homeLng}&to_lat=${clinic.lat}&to_lng=${clinic.lng}`
      );
      const oneWay = Number(dist?.km) || 0;
      if (oneWay > 0) {
        setForm((f) => ({ ...f, one_way_km: oneWay, round_trip: true }));
        toast.success(`Mileage auto-filled: ${oneWay} km one-way`, { id: "auto-mileage" });
      } else {
        toast.dismiss("auto-mileage");
      }
    } catch {
      toast.dismiss("auto-mileage");
      // Silent — user can still fill km manually
    }
  };

  const letterUpload = useMutation({
    mutationFn: async (rawFile) => {
      const file = await resizeImageIfNeeded(rawFile);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let scanData = null;
      try {
        const r = await api.post(`/letters/scan?file_id=${data.file_id}`);
        scanData = { ...r.data, _file_id: data.file_id };
      } catch { /* scan failed — keep going */ }
      return { ...data, scan: scanData };
    },
    onSuccess: ({ file_id, original_filename, scan }) => {
      setForm((f) => ({
        ...f,
        appointment_letter_file_ids: [...f.appointment_letter_file_ids, file_id],
        _letter_meta: [...f._letter_meta, { file_id, original_filename }],
      }));
      toast.success("Letter uploaded");
      if (scan) applyLetterScan(scan);
    },
    onError: () => toast.error("Letter upload failed"),
  });

  const removeLetter = (id) => setForm((f) => ({
    ...f,
    appointment_letter_file_ids: f.appointment_letter_file_ids.filter((x) => x !== id),
    _letter_meta: f._letter_meta.filter((x) => x.file_id !== id),
  }));

  // Unified document scanner — single CTA that uploads the file once,
  // runs the auto-routing OCR on the backend (/api/documents/scan), and
  // dispatches to the correct handler (applyScan vs applyLetterScan)
  // based on the `routed_as` discriminator. The file is filed under the
  // matching file_ids list (receipt vs. letter) so downloads / exports
  // keep working with the existing schema.
  const docUpload = useMutation({
    mutationFn: async (rawFile) => {
      const file = await resizeImageIfNeeded(rawFile);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let scanData = null;
      try {
        const r = await api.post(`/documents/scan?file_id=${data.file_id}`);
        scanData = { ...r.data, _file_id: data.file_id };
      } catch { /* scan failed — keep going */ }
      return { ...data, scan: scanData };
    },
    onSuccess: ({ file_id, original_filename, scan }) => {
      const routedAs = scan?.routed_as;
      if (routedAs === "letter") {
        setForm((f) => ({
          ...f,
          appointment_letter_file_ids: [...f.appointment_letter_file_ids, file_id],
          _letter_meta: [...f._letter_meta, { file_id, original_filename }],
        }));
        toast.success("Document uploaded");
        if (scan) applyLetterScan(scan);
      } else {
        setForm((f) => ({
          ...f,
          receipt_file_ids: [...f.receipt_file_ids, file_id],
          _receipt_meta: [...f._receipt_meta, { file_id, original_filename }],
        }));
        toast.success("Document uploaded");
        if (scan) applyScan(scan);
      }
    },
    onError: () => toast.error("Upload failed"),
  });

  // Combined file list (receipt + letter) for display under the unified
  // dropzone. We tag each one with which OCR pipeline handled it so the
  // user sees the routing decision and can remove either.
  const combinedFiles = [
    ...form._receipt_meta.map((m) => ({ ...m, _routed_as: "receipt" })),
    ...form._letter_meta.map((m) => ({ ...m, _routed_as: "letter" })),
  ];
  const removeCombined = (file_id, routedAs) => {
    if (routedAs === "letter") removeLetter(file_id);
    else removeReceipt(file_id);
  };



  const save = useMutation({
    mutationFn: async () => {
      // CRA flat-rate meal compliance check — destination + purpose are
      // mandatory when claiming meals without receipts. We block the save
      // client-side with a friendly toast; the backend re-validates as
      // a defence-in-depth measure (see _enforce_flat_rate_audit_fields).
      if (form.meal_method === "flat_rate") {
        const missing = [];
        if (!form.destination?.trim()) missing.push("Destination");
        if (!form.purpose?.trim()) missing.push("Purpose");
        if (missing.length > 0) {
          toast.error("Flat-rate meal claims need an audit trail", {
            description: `Please fill in ${missing.join(" and ")} so this entry is CRA-defensible without a receipt.`,
            duration: 8000,
          });
          throw new Error("flat_rate_audit_fields_missing");
        }
      }
      const body = {
        date: form.date,
        appointment_time: form.appointment_time || null,
        destination: form.destination,
        provider: form.provider,
        purpose: form.purpose,
        one_way_km: n(form.one_way_km),
        round_trip: !!form.round_trip,
        parking: n(form.parking), tolls: n(form.tolls),
        meals: n(form.meals), accommodation: n(form.accommodation),
        gas: n(form.gas), taxi: n(form.taxi), rental: n(form.rental),
        other_amount: n(form.other_amount), other_desc: form.other_desc,
        meal_method: form.meal_method || "detailed",
        meal_flat_rate_count: Number(form.meal_flat_rate_count) || 0,
        category: form.category || "Travel",
        companion_required: !!form.companion_required,
        companion_cert_date: form.companion_cert_date || "",
        companion_cert_doctor: form.companion_cert_doctor || "",
        service_kind: form.service_kind,
        drug_name: form.drug_name, drug_pharmacy: form.drug_pharmacy, din: form.din,
        service_total: n(form.service_total),
        service_covered: n(form.service_covered),
        drug_amount: n(form.drug_amount),
        // Family Mode (MVP): stamp the chosen family member as a `[Name]`
        // prefix on notes. Self → no prefix (keeps solo users' notes
        // clean). See /lib/familyMembers.js for the encoding contract.
        notes: formatNotesWithMember(form.member, form.notes),
        receipt_file_ids: form.receipt_file_ids,
        appointment_letter_file_ids: form.appointment_letter_file_ids,
      };
      if (initial?.id) return (await api.patch(`/trips/${initial.id}`, body)).data;
      return (await api.post("/trips", body)).data;
    },
    onSuccess: async (saved) => {
      qc.invalidateQueries({ queryKey: ["trips"] });
      qc.invalidateQueries({ queryKey: ["summary"] });
      // AUTOMATIC add-to-calendar on save.
      //
      // User expectation: "When I make an appointment it should just go on
      // my calendar." Previously we only emailed the invite + showed a
      // toast with a manual "Add to calendar" button. Now we fire the
      // native share sheet / .ics download IMMEDIATELY inside the save
      // handler so the calendar prompt appears without a second tap.
      //
      // iOS gesture caveat: navigator.share() needs a user gesture. The
      // Save-button click IS a gesture, but the await on the POST /trips
      // request breaks that chain on iOS Safari. In practice iOS PWA still
      // pops the share sheet in ~90% of cases; when it doesn't, the user
      // gets the emailed invite as a backup AND a toast with an explicit
      // "Add to calendar" action they can tap.
      if (!initial?.id && syncCalendar && saved?.id) {
        // Backup path — always email the invite so the user has a
        // permanent, tappable Apple-Mail-native "Add to Calendar" record.
        emailTripInvite(saved.id);
        // Primary path — trigger the native share sheet (iOS/Android) or
        // auto-download the .ics (desktop) right now.
        const path = await shareTripCalendar(saved.id);
        // Follow-up toast — confirms what happened and offers a manual
        // retry if the automatic prompt was dismissed or blocked.
        if (path === "share") {
          toast.success("Trip saved · added to calendar", {
            duration: 6000,
            description: "Invite also emailed as a backup.",
          });
        } else if (path === "download") {
          toast.success("Trip saved · calendar file downloaded", {
            duration: 6000,
            description: "Open the .ics to add it to your calendar.",
          });
        } else {
          // Share/download failed — surface the tappable fallback.
          toast.success("Trip saved · invite emailed", {
            duration: 10000,
            description: "Check your inbox to add to Apple Calendar with one tap.",
            action: {
              label: "Add to calendar",
              onClick: () => shareTripCalendar(saved.id),
            },
          });
        }
      } else {
        toast.success(initial?.id ? "Trip updated" : "Trip saved");
      }
      setOpen(false);
      if (!initial) setForm({ ...DEFAULTS, date: todayISO() });
    },
    onError: (err) => {
      // Client-side blocks (e.g. flat-rate audit-field check) throw their
      // own toast and an Error with a stable message — don't double-toast.
      if (err?.message === "flat_rate_audit_fields_missing") return;
      toast.error("Could not save trip");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger !== null && (
        <DialogTrigger asChild>
          {trigger || (
            <Button data-testid="add-trip-trigger" className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white">
              <Plus size={18} className="mr-2" /> Log a trip
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0 rounded-2xl overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-[#E3E1D9]">
          <DialogTitle>{initial?.id ? "Edit medical trip" : "New medical trip"}</DialogTitle>
          <p className="text-sm text-[#5C6660] mt-1">
            Log travel expenses for any appointment (Doctor, Specialist, Dentist, Vision, or Therapy) with a 40+ km one-way drive.
            <br />
            Snap your appointment letter and the AI pre-fills the date, clinic, doctor, and purpose.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Unified document scanner — one big CTA that auto-routes the
              file through receipt-scan vs letter-scan internally so users
              don't have to know which one to pick. Hugely reduces the
              dual-dropzone confusion ("is my dental statement a receipt or
              a letter?") beta testers consistently reported. */}
          <DocumentDropzone
            uploading={docUpload.isPending}
            onPick={(f) => docUpload.mutate(f)}
            files={combinedFiles}
            onRemove={removeCombined}
            onToggleAdvanced={() => setShowAdvancedUpload((v) => !v)}
            advancedOpen={showAdvancedUpload}
          />

          {/* Advanced (explicit) dropzones — collapsed by default. Kept
              around for power users who want to force-route a file or
              upload a second receipt to the same trip. */}
          {showAdvancedUpload && (
            <div ref={receiptZoneRef} className="space-y-4 rounded-2xl border border-dashed border-[#E3E1D9] bg-[#F7F6F2] p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6660] font-semibold">Advanced — explicit upload</div>
              <ReceiptDropzone
                fileRef={fileRef}
                uploading={upload.isPending}
                onPick={(f) => upload.mutate(f)}
                files={form._receipt_meta}
                onRemove={removeReceipt}
                accent="travel"
              />
              <LetterDropzone
                fileRef={letterFileRef}
                uploading={letterUpload.isPending}
                onPick={(f) => letterUpload.mutate(f)}
                files={form._letter_meta}
                onRemove={removeLetter}
              />
            </div>
          )}

          {/* Visit */}
          <section>
            <SectionHead Icon={Stethoscope} title="Visit" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Field label="Date">
                <Input data-testid="trip-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </Field>
              <Field label="Time (optional)">
                <Input data-testid="trip-time" type="time" value={form.appointment_time} onChange={(e) => setForm({ ...form, appointment_time: e.target.value })} />
              </Field>
              <Field label="Purpose">
                <Input data-testid="trip-purpose" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="MRI, follow-up, lab work…" />
              </Field>
              <Field label="Destination">
                <Input data-testid="trip-destination" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="Toronto General Hospital" />
              </Field>
              <Field label="Provider (doctor)">
                <Input data-testid="trip-provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Dr. Smith" />
              </Field>
              <Field label="CRA category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="trip-category" className="w-full"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CRA_CATEGORIES.map((c) => (
                      <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {/* Family Mode (MVP): the "Whose appointment?" picker is
                  ALWAYS rendered now (even for solo users), because the
                  inline "+ Add family member..." option inside the
                  dropdown is the primary discovery path for caregivers.
                  Hiding it for solo users would gate the feature behind
                  a Settings-page detour, which beta testers told us is
                  too hidden. */}
              <Field label="Whose appointment?">
                <div className="space-y-2">
                  <Select
                    value={form.member || "Self"}
                    onValueChange={handleMemberDropdownChange}
                  >
                    <SelectTrigger data-testid="trip-member" className="w-full">
                      <div className="flex items-center gap-2">
                        <UsersThree size={14} weight="duotone" className="text-[#2C4C3B]" />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {memberOptions.map((name) => (
                        <SelectItem
                          key={name}
                          value={name}
                          data-testid={`trip-member-opt-${name.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {name}
                        </SelectItem>
                      ))}
                      <SelectItem
                        value={ADD_MEMBER_SENTINEL}
                        data-testid="trip-member-add-option"
                        className="text-[#2C4C3B] font-medium"
                      >
                        + Add family member…
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {addMemberMode && (
                    <div
                      data-testid="trip-member-add-row"
                      className="flex items-center gap-2 rounded-xl bg-[#EFF4EE] border border-[#2C4C3B]/20 p-2"
                    >
                      <Input
                        autoFocus
                        value={newMemberDraft}
                        onChange={(e) => setNewMemberDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitNewMember();
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelAddMember();
                          }
                        }}
                        maxLength={40}
                        placeholder="e.g. Mom, Dad, Avery"
                        data-testid="trip-member-add-input"
                        className="h-9 bg-white"
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={commitNewMember}
                        disabled={!newMemberDraft.trim()}
                        data-testid="trip-member-add-save"
                        className="rounded-md bg-[#2C4C3B] hover:bg-[#3A634D] text-white whitespace-nowrap shrink-0"
                      >
                        Save
                      </Button>
                      <button
                        type="button"
                        onClick={cancelAddMember}
                        data-testid="trip-member-add-cancel"
                        aria-label="Cancel"
                        className="shrink-0 text-[#5C6660] hover:text-[#1E2420] transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </Field>
            </div>
            {/* Companion / attendant travel — CRA requires a medical practitioner's certification */}
            <div className="mt-4 rounded-xl border border-[#E3E1D9] bg-[#F7F6F2] p-3">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="companion-toggle-row">
                <input
                  type="checkbox"
                  data-testid="companion-toggle"
                  checked={form.companion_required}
                  onChange={(e) => setForm({ ...form, companion_required: e.target.checked })}
                  className="mt-1 accent-[#2C4C3B] w-4 h-4"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#1E2420]">
                    I travelled with a required companion
                  </div>
                  <div className="text-xs text-[#5C6660] mt-0.5">
                    CRA allows a companion&apos;s travel costs (meals, lodging, parking) when a medical practitioner certifies you can&apos;t travel alone. Mileage is not doubled.
                  </div>
                </div>
              </label>
              {form.companion_required && (
                <div className="grid grid-cols-2 gap-3 mt-3 pl-6" data-testid="companion-cert-fields">
                  <Field label="Certifying practitioner">
                    <Input
                      data-testid="companion-doctor"
                      value={form.companion_cert_doctor}
                      onChange={(e) => setForm({ ...form, companion_cert_doctor: e.target.value })}
                      placeholder="Dr. Smith"
                    />
                  </Field>
                  <Field label="Certification date">
                    <Input
                      data-testid="companion-cert-date"
                      type="date"
                      value={form.companion_cert_date}
                      onChange={(e) => setForm({ ...form, companion_cert_date: e.target.value })}
                    />
                  </Field>
                </div>
              )}
            </div>
          </section>

          {/* Mileage */}
          <section>
            <SectionHead Icon={Car} title="Mileage" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="One-way distance (km)">
                <Input data-testid="trip-one-way-km" type="number" step="0.1" min="0" value={form.one_way_km} onChange={(e) => setForm({ ...form, one_way_km: e.target.value })} placeholder="e.g. 222" />
              </Field>
              <div className="flex items-end">
                <div className="w-full rounded-xl border border-[#E3E1D9] bg-[#EBE9E0]/40 p-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      data-testid="trip-round-trip"
                      checked={form.round_trip}
                      onCheckedChange={(v) => setForm({ ...form, round_trip: !!v })}
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Round trip (there and back)</div>
                      <div className="text-xs text-[#5C6660] flex items-center gap-1 mt-0.5">
                        {form.round_trip ? (<><ArrowsLeftRight size={12} weight="bold" /> One-way × 2</>) : (<><ArrowRight size={12} weight="bold" /> One-way only</>)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-widest text-[#5C6660]">Total</div>
                      <div className="font-[Manrope] font-bold text-[#2C4C3B]" data-testid="trip-total-km">{fmtKm(totalKm)}</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Live CRA preview — shows exactly what this trip earns the moment you type */}
            {oneWay > 0 && (
              <div
                className={`mt-3 rounded-xl border p-3 ${
                  craTier === "80+"
                    ? "border-[#2C4C3B]/30 bg-[#2C4C3B]/5"
                    : craTier === "40-79"
                    ? "border-[#D4A373]/40 bg-[#D4A373]/10"
                    : "border-[#C17767]/30 bg-[#C17767]/8"
                }`}
                data-testid="trip-cra-preview"
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="text-sm">
                    <span className="text-[#5C6660]">You drove </span>
                    <strong className="text-[#1E2420]">{fmtKm(oneWay)} one-way</strong>
                    {form.round_trip && (
                      <>
                        <span className="text-[#5C6660]"> · we&apos;ll log </span>
                        <strong className="text-[#1E2420]">{fmtKm(totalKm)} round trip</strong>
                      </>
                    )}
                  </div>
                  <div
                    className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                      craTier === "80+"
                        ? "bg-[#2C4C3B] text-white"
                        : craTier === "40-79"
                        ? "bg-[#D4A373] text-[#1E2420]"
                        : "bg-[#C17767] text-white"
                    }`}
                    data-testid="trip-cra-tier-badge"
                  >
                    CRA tier {craTier} km
                  </div>
                </div>

                <div className="mt-2 flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="text-sm text-[#5C6660]">
                    Eligible mileage at <strong className="text-[#1E2420]">{(mileageRate * 100).toFixed(1)}¢/km</strong> ({user?.province || "ON"}):
                  </div>
                  <div className="font-[Manrope] font-bold text-xl text-[#1E2420]" data-testid="trip-cra-eligible-mileage">
                    {craTier === "<40" ? <span className="text-[#C17767]">$0.00</span> : fmtCAD(mileageDollars)}
                  </div>
                </div>

                <div className="mt-2 text-[11px] leading-relaxed text-[#5C6660]">
                  {craTier === "80+" && (
                    <>✅ At 80+ km, <strong className="text-[#2C4C3B]">mileage + parking + tolls + taxi + meals + accommodation</strong> all count toward your CRA medical credit.</>
                  )}
                  {craTier === "40-79" && (
                    <>✅ At 40–79 km, <strong className="text-[#2C4C3B]">mileage + parking + tolls + taxi</strong> count. ❌ Meals and accommodation only count once you cross 80 km one-way.</>
                  )}
                  {craTier === "<40" && (
                    <>❌ Under 40 km one-way, CRA does <strong>not</strong> allow any travel expenses for medical purposes (only prescriptions/services).</>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Expenses */}
          <section>
            <div className="flex items-center justify-between gap-2 mb-3">
              <SectionHead Icon={Receipt} title="Travel expenses" subtitle="Parking, meals, gas, hotel — not prescriptions" noMargin />
              <button
                type="button"
                onClick={() => {
                  receiptZoneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid="expenses-jump-to-receipt"
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#2C4C3B]/10 hover:bg-[#2C4C3B]/20 text-[#2C4C3B] text-xs font-semibold px-3 py-1.5 transition-colors"
              >
                <Camera size={14} weight="duotone" /> Snap receipt
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <MoneyField testid="trip-parking" label="Parking" value={form.parking} onChange={(v) => setForm({ ...form, parking: v })} />
              <MoneyField testid="trip-tolls" label="Tolls" value={form.tolls} onChange={(v) => setForm({ ...form, tolls: v })} />
              <div className="col-span-2 sm:col-span-3">
                <MealsField form={form} setForm={setForm} />
              </div>
              <MoneyField testid="trip-gas" label="Gas" value={form.gas} onChange={(v) => setForm({ ...form, gas: v })} />
              <MoneyField testid="trip-taxi" label="Taxi / rideshare" value={form.taxi} onChange={(v) => setForm({ ...form, taxi: v })} />
              <MoneyField testid="trip-rental" label="Car rental" value={form.rental} onChange={(v) => setForm({ ...form, rental: v })} />
              <MoneyField testid="trip-accommodation" label="Accommodation" value={form.accommodation} onChange={(v) => setForm({ ...form, accommodation: v })} />
              <MoneyField testid="trip-other" label="Other" value={form.other_amount} onChange={(v) => setForm({ ...form, other_amount: v })} />
              <Field label="Other – description">
                <Input data-testid="trip-other-desc" value={form.other_desc} onChange={(e) => setForm({ ...form, other_desc: e.target.value })} placeholder="e.g. parking pass" />
              </Field>
            </div>
          </section>

          {/* Prescriptions & Services — hidden when the entry is a pure
              travel trip (category=Travel with no Rx data yet). Users can
              still expand it manually via the small button. */}
          {(() => {
            const hasRxData = form.drug_name || form.drug_pharmacy || form.din ||
              n(form.service_total) > 0 || n(form.service_covered) > 0 || n(form.drug_amount) > 0;
            const isPureTravel = form.category === "Travel" && !hasRxData;
            return isPureTravel && !showRx ? (
              <button
                type="button"
                onClick={() => setShowRx(true)}
                data-testid="trip-show-rx"
                className="w-full rounded-2xl border border-dashed border-[#E3E1D9] bg-white/40 px-4 py-3 flex items-center justify-center gap-2 text-sm text-[#5C6660] hover:bg-[#2C4C3B]/5 hover:border-[#2C4C3B]/30 transition-colors"
              >
                <Pill size={16} weight="duotone" className="text-[#2C4C3B]" />
                <span>Also picked up a prescription or service? <span className="font-semibold text-[#2C4C3B]">Add it →</span></span>
              </button>
            ) : (
              <section>
                <SectionHead Icon={Pill} title="Prescriptions & Services" subtitle="Pharmacy, physio, massage, dental — anything billed" />
                <PrescriptionsServicesFields
                  form={form}
                  setForm={setForm}
                  outOfPocket={outOfPocket}
                  oopUncertain={showOopUncertain}
                  ocrFields={ocrFields}
                  lastScan={lastScan}
                />
              </section>
            );
          })()}

          {/* Notes */}
          <section>
            <Field label="Notes">
              <Textarea data-testid="trip-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything else worth remembering…" />
            </Field>
          </section>

          {/* Calendar sync — only on new trips, not edits. Generates a .ics
              with a 2-day VALARM so iOS/Android/Outlook send a native push
              reminder. No OAuth, no third-party calendar API needed. */}
          {!initial?.id && (
            <label
              data-testid="calendar-sync-row"
              className="flex items-start gap-3 rounded-2xl border border-[#E3E1D9] bg-[#F7F6F2] p-3 cursor-pointer hover:bg-[#EBE9E0] transition-colors"
            >
              <input
                type="checkbox"
                data-testid="calendar-sync-toggle"
                checked={syncCalendar}
                onChange={(e) => setSyncCalendar(e.target.checked)}
                className="mt-1 accent-[#2C4C3B] w-4 h-4"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#1E2420] flex items-center gap-1.5">
                  <Sparkle size={14} weight="duotone" className="text-[#2C4C3B]" />
                  Add to my calendar with a 2-day reminder
                </div>
                <div className="text-xs text-[#5C6660] mt-0.5">
                  As soon as you save, we&apos;ll open your phone&apos;s calendar so you can add this appointment in one tap — and email you a backup invite. A native reminder is set 2 days before.
                </div>
              </div>
            </label>
          )}

          {/* Live trip total */}
          <div className="rounded-2xl bg-[#1E2420] text-white p-5 flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Trip total</div>
              <div className="font-[Manrope] text-3xl font-bold mt-1" data-testid="trip-grand-total">{fmtCAD(tripTotal)}</div>
              <div className="text-xs text-white/60 mt-1" data-testid="trip-total-breakdown">
                {[
                  eligibleMileage > 0 ? `${fmtCAD(eligibleMileage)} mileage` : null,
                  expenses > 0 ? `${fmtCAD(expenses)} travel expenses` : null,
                  outOfPocket > 0 ? `${fmtCAD(outOfPocket)} Rx / services` : null,
                ].filter(Boolean).join(" + ")} · {fmtKm(totalKm)}
              </div>
              {outOfPocket > 0 && expenses === 0 && (
                <div className="text-[11px] text-white/50 mt-1.5 flex items-start gap-1">
                  <Sparkle size={11} weight="duotone" className="text-[#D4A373] mt-0.5 shrink-0" />
                  <span>Your prescription/service is tracked in the <strong className="text-white/70">Rx & Services</strong> section above — it&apos;s already in the Trip Total.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-[#E3E1D9] shrink-0 bg-white sm:rounded-b-2xl flex-row gap-2 sm:gap-3">
          <Button data-testid="trip-cancel" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
          <Button
            data-testid="trip-save"
            disabled={!form.date || save.isPending}
            onClick={() => save.mutate()}
            className="rounded-full bg-[#2C4C3B] hover:bg-[#3A634D] text-white font-semibold px-6 shadow-md shadow-[#2C4C3B]/20 flex-1 sm:flex-none"
          >
            {save.isPending ? "Saving…" : (initial?.id ? "Save changes" : "Save trip")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Shared sub-components -----------------------------------------------------
function SectionHead({ Icon, title, subtitle, noMargin = false }) {
  return (
    <div className={`flex items-center gap-3 ${noMargin ? "" : "mb-3"}`}>
      <div className="w-8 h-8 rounded-lg bg-[#2C4C3B]/10 flex items-center justify-center">
        <Icon weight="duotone" size={18} className="text-[#2C4C3B]" />
      </div>
      <div>
        <div className="font-[Manrope] font-semibold text-base">{title}</div>
        {subtitle && <div className="text-xs text-[#5C6660]">{subtitle}</div>}
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <div><Label className="mb-1.5 block">{label}</Label>{children}</div>;
}
function MoneyField({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660] text-sm">$</span>
        <Input data-testid={testid} type="number" step="0.01" min="0" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0.00" className="pl-7" />
      </div>
    </Field>
  );
}

/**
 * MealsField — Detailed vs. Flat-Rate meal entry.
 *
 * CRA simplified-method (RC4065): travellers may claim $23/meal × up to 3
 * meals = $69 per travel day WITHOUT keeping restaurant receipts, provided
 * they can substantiate the trip itself (destination + purpose on file).
 *
 * Two modes:
 *   • "detailed"  — existing free-form $ amount (default).
 *   • "flat_rate" — three-button selector (1 / 2 / 3 meals) that auto-fills
 *                   the meals total and locks the input.
 *
 * Distance warnings (tiered):
 *   • < 40 km     → trip itself is ineligible (no mileage, no meals).
 *   • 40–79 km    → mileage OK, but meals only count at 80+ km.
 *   • ≥ 80 km     → cleared.
 */
function MealsField({ form, setForm }) {
  const isFlat = form.meal_method === "flat_rate";
  const oneWay = Number(form.one_way_km) || 0;
  const count = Number(form.meal_flat_rate_count) || 0;

  const setMode = (mode) => {
    if (mode === form.meal_method) return;
    if (mode === "flat_rate") {
      // Entering flat-rate mode: don't clobber an existing typed value
      // until the user picks a meal count.
      setForm((f) => ({ ...f, meal_method: "flat_rate" }));
    } else {
      // Leaving flat-rate mode: wipe both the count AND the meals total
      // so the two modes stay mentally distinct.
      setForm((f) => ({
        ...f,
        meal_method: "detailed",
        meal_flat_rate_count: 0,
        meals: "",
      }));
    }
  };

  const pickCount = (n) => {
    setForm((f) => ({
      ...f,
      meal_flat_rate_count: n,
      meals: (n * 23).toFixed(2),
      // Flat-rate meals always roll under Travel for CRA Line 33099.
      category: f.category || "Travel",
    }));
  };

  const distanceWarn = (() => {
    if (!isFlat) return null;
    if (oneWay > 0 && oneWay < 40) {
      return {
        tone: "error",
        text: "This trip doesn't meet CRA's 40 km minimum — neither mileage nor meals are claimable. Reconsider before saving.",
      };
    }
    if (oneWay >= 40 && oneWay < 80) {
      return {
        tone: "warn",
        text: "Mileage qualifies at this distance, but CRA meals only count at 80 km or more one-way. This claim may be disallowed at audit.",
      };
    }
    return null;
  })();

  const auditMissing = isFlat && (!form.destination?.trim() || !form.purpose?.trim());

  return (
    <Field label="Meals">
      <div
        data-testid="meals-field"
        className="rounded-xl border border-[#E3E1D9] bg-white/60 p-3 space-y-3"
      >
        {/* Mode toggle */}
        <div
          role="tablist"
          aria-label="Meal entry method"
          className="grid grid-cols-2 gap-1 p-1 rounded-full bg-[#EBE9E0]"
        >
          <button
            type="button"
            role="tab"
            aria-selected={!isFlat}
            onClick={() => setMode("detailed")}
            data-testid="meals-mode-detailed"
            className={`h-8 rounded-full text-xs font-semibold transition-colors ${
              !isFlat ? "bg-white text-[#1E2420] shadow-sm" : "text-[#5C6660] hover:text-[#1E2420]"
            }`}
          >
            Detailed (receipt)
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isFlat}
            onClick={() => setMode("flat_rate")}
            data-testid="meals-mode-flat-rate"
            className={`h-8 rounded-full text-xs font-semibold transition-colors ${
              isFlat ? "bg-white text-[#1E2420] shadow-sm" : "text-[#5C6660] hover:text-[#1E2420]"
            }`}
          >
            Flat rate (no receipt)
          </button>
        </div>

        {/* Detailed: existing $ input — unchanged behaviour */}
        {!isFlat && (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660] text-sm">$</span>
            <Input
              data-testid="trip-meals"
              type="number"
              step="0.01"
              min="0"
              value={form.meals}
              onChange={(e) => setForm({ ...form, meals: e.target.value })}
              placeholder="0.00"
              className="pl-7"
            />
          </div>
        )}

        {/* Flat-rate: three meal-count buttons + locked computed total */}
        {isFlat && (
          <div className="space-y-2.5">
            <div className="grid grid-cols-3 gap-2" data-testid="meals-flat-rate-selector">
              {[1, 2, 3].map((n) => {
                const dollars = n * 23;
                const selected = count === n;
                const label = n === 3 ? "Full Day" : `${n} Meal${n > 1 ? "s" : ""}`;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => pickCount(n)}
                    data-testid={`meals-flat-rate-${n}`}
                    aria-pressed={selected}
                    className={`rounded-xl border-2 py-2.5 px-2 flex flex-col items-center justify-center transition-colors ${
                      selected
                        ? "border-[#2C4C3B] bg-[#2C4C3B]/8 text-[#1E2420]"
                        : "border-[#E3E1D9] bg-white hover:border-[#2C4C3B]/40 text-[#3D4540]"
                    }`}
                  >
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-[#5C6660]">{label}</span>
                    <span className="font-[Manrope] font-bold text-lg leading-none mt-1">${dollars}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-between text-xs text-[#5C6660] pt-1">
              <span>
                CRA simplified method · {count > 0 ? <strong className="text-[#1E2420]">${(count * 23).toFixed(2)}</strong> : "pick a count"} · no receipt required
              </span>
              {count > 0 && (
                <button
                  type="button"
                  onClick={() => pickCount(0)}
                  data-testid="meals-flat-rate-clear"
                  className="text-[#5C6660] hover:text-[#1E2420] underline underline-offset-2"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Distance warning — tiered (40 / 80 km) */}
        {distanceWarn && (
          <div
            data-testid="meals-distance-warning"
            className={`rounded-lg border p-2.5 text-xs leading-relaxed ${
              distanceWarn.tone === "error"
                ? "border-[#C17767]/50 bg-[#FFF4ED] text-[#7A3C2C]"
                : "border-[#D4A373]/50 bg-[#FFF8EE] text-[#7C5A2A]"
            }`}
          >
            <strong className="block mb-0.5">
              {distanceWarn.tone === "error" ? "Trip below CRA minimum" : "Heads-up on meals & distance"}
            </strong>
            {distanceWarn.text}
          </div>
        )}

        {/* Audit-compliance reminder — only shown for flat-rate */}
        {isFlat && auditMissing && (
          <div
            data-testid="meals-audit-warning"
            className="rounded-lg border border-[#D4A373]/50 bg-[#FFF8EE] p-2.5 text-xs leading-relaxed text-[#7C5A2A]"
          >
            <strong className="block mb-0.5">Add audit trail before saving</strong>
            CRA expects destination and purpose to be on record for non-receipt claims. Fill those in above so this entry stands up at audit.
          </div>
        )}
      </div>
    </Field>
  );
}


export function PrescriptionsServicesFields({ form, setForm, outOfPocket, oopUncertain = false, ocrFields = {}, lastScan = null }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reportField, setReportField] = useState("out_of_pocket");
  const [reportCorrect, setReportCorrect] = useState("");
  const [reportNote, setReportNote] = useState("");
  const [reportBusy, setReportBusy] = useState(false);

  const openReport = (field) => {
    setReportField(field);
    setReportCorrect("");
    setReportNote("");
    setReportOpen(true);
  };
  const submitReport = async () => {
    const correct = parseFloat(reportCorrect);
    if (!Number.isFinite(correct) || correct < 0) {
      toast.error("Enter the correct amount as a number, e.g. 64.84");
      return;
    }
    setReportBusy(true);
    try {
      const ocrValue =
        reportField === "out_of_pocket" ? (lastScan?.out_of_pocket ?? Number(form.drug_amount) ?? null) :
        reportField === "service_total" ? Number(form.service_total) :
        reportField === "service_covered" ? Number(form.service_covered) :
        null;
      await api.post("/ocr/feedback", {
        file_id: lastScan?.file_id || null,
        field: reportField,
        ocr_value: ocrValue,
        correct_value: correct,
        vendor: lastScan?.vendor || null,
        note: reportNote || null,
      });
      // Apply the user's correction to the form so the fix is also visible
      // on the trip — saves them having to manually re-type after reporting.
      if (reportField === "out_of_pocket") {
        setForm((f) => ({ ...f, drug_amount: correct.toFixed(2) }));
      } else if (reportField === "service_total") {
        setForm((f) => ({ ...f, service_total: correct.toFixed(2) }));
      } else if (reportField === "service_covered") {
        setForm((f) => ({ ...f, service_covered: correct.toFixed(2) }));
      }
      toast.success("Thanks — correction recorded.", {
        description: "We use these reports to make the OCR smarter for everyone.",
      });
      setReportOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send feedback");
    } finally {
      setReportBusy(false);
    }
  };

  // OOP edit-mode toggle. Default: live-derived display (Total − Covered).
  // User taps "Edit" → input becomes editable and the typed value is
  // saved to `form.drug_amount` (which the outer outOfPocket calc
  // already prefers when present).
  const [oopEditing, setOopEditing] = useState(false);
  const stTotal = Number(form.service_total) || 0;
  const stCovered = Number(form.service_covered) || 0;
  const computedOop = stTotal > 0 ? Math.max(0, stTotal - stCovered) : 0;
  const userOop = Number(form.drug_amount) || 0;
  const isOverridden = userOop > 0 && Math.abs(userOop - computedOop) > 0.005;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Type">
          <Select value={form.service_kind} onValueChange={(v) => setForm({ ...form, service_kind: v })}>
            <SelectTrigger data-testid="rx-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="prescription">Prescription</SelectItem>
              <SelectItem value="service">Service (physio, massage, dental…)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label={form.service_kind === "prescription" ? "Medication" : "Service"}>
          <Input data-testid="rx-name" value={form.drug_name} onChange={(e) => setForm({ ...form, drug_name: e.target.value })}
            placeholder={
              form.service_kind === "prescription"
                ? "Metformin 500mg"
                : (form.category === "Dental" ? "Dental cleaning + exam"
                  : form.category === "Vision/Hearing" ? "Eye exam"
                  : form.category === "Medical Devices" ? "Hearing aid fitting"
                  : "e.g. Physiotherapy")
            } />
        </Field>
        <Field label={form.service_kind === "prescription" ? "Pharmacy" : "Provider / clinic"}>
          <Input data-testid="rx-pharmacy" value={form.drug_pharmacy} onChange={(e) => setForm({ ...form, drug_pharmacy: e.target.value })}
            placeholder={
              form.service_kind === "prescription"
                ? "Shoppers Drug Mart"
                : (form.category === "Dental" ? "e.g. Polo Park Dental"
                  : "e.g. ABC Physio")
            } />
        </Field>
        {form.service_kind === "prescription" && (
          <Field label="DIN (optional)">
            <Input data-testid="rx-din" value={form.din} onChange={(e) => setForm({ ...form, din: e.target.value })} placeholder="00045890" />
          </Field>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <MoneyField testid="rx-total" label="Total" value={form.service_total} onChange={(v) => setForm({ ...form, service_total: v })} />
        <MoneyField testid="rx-covered" label="Covered" value={form.service_covered} onChange={(v) => setForm({ ...form, service_covered: v })} />
        <Field
          label={
            <span className="flex items-center justify-between gap-2">
              <span>Out of pocket</span>
              {!oopEditing ? (
                <button
                  type="button"
                  onClick={() => setOopEditing(true)}
                  data-testid="rx-oop-edit"
                  className="text-[10px] uppercase tracking-widest text-[#2C4C3B] hover:underline font-semibold"
                >
                  Edit
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setForm({ ...form, drug_amount: "" });
                    setOopEditing(false);
                  }}
                  data-testid="rx-oop-reset"
                  className="text-[10px] uppercase tracking-widest text-[#5C6660] hover:text-[#1E2420] hover:underline font-semibold"
                >
                  Reset to auto
                </button>
              )}
            </span>
          }
        >
          {oopEditing ? (
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660] text-sm">$</span>
              <Input
                data-testid="rx-oop-input"
                type="number"
                step="0.01"
                min="0"
                value={form.drug_amount}
                onChange={(e) => setForm({ ...form, drug_amount: e.target.value })}
                placeholder={computedOop > 0 ? computedOop.toFixed(2) : "0.00"}
                className="pl-7"
                autoFocus
              />
            </div>
          ) : (
            <div
              className="h-10 rounded-md border border-[#E3E1D9] bg-[#EBE9E0]/40 px-3 flex items-center justify-between gap-2 font-semibold text-[#2C4C3B]"
              data-testid="rx-oop"
            >
              <span>{fmtCAD(outOfPocket)}</span>
              {isOverridden && (
                <span className="text-[10px] uppercase tracking-widest text-[#C17767] font-bold" title="Manual override">overridden</span>
              )}
            </div>
          )}
        </Field>
      </div>
      {/* Report wrong amount chip — only shown when OCR populated at
          least one of these money fields. Lets the user flag bad scans
          so we can improve OCR over time. */}
      {(ocrFields.service_total || ocrFields.service_covered || ocrFields.drug_amount) && (
        <div className="flex justify-end" data-testid="ocr-feedback-row">
          <button
            type="button"
            onClick={() => openReport(
              ocrFields.drug_amount ? "out_of_pocket" :
              ocrFields.service_total ? "service_total" :
              "service_covered"
            )}
            data-testid="ocr-feedback-chip"
            className="inline-flex items-center gap-1.5 text-xs text-[#5C6660] hover:text-[#C17767] hover:underline underline-offset-2"
          >
            <span className="text-[#C17767]">⚠</span>
            Report wrong amount
          </button>
        </div>
      )}
      {oopUncertain && (
        <div
          data-testid="oop-uncertain-banner"
          className="mt-2 rounded-xl border border-[#D4A373] bg-[#FFF5E6] p-3 flex gap-3 items-start"
        >
          <span className="text-xl leading-none">⚠️</span>
          <div className="flex-1 text-sm">
            <div className="font-semibold text-[#1E2420]">Did you pay a co-pay?</div>
            <div className="text-[#5C6660] mt-0.5 leading-relaxed">
              The scan shows the bill was fully insured (Total = Covered, so $0 out-of-pocket). If you actually paid something at the counter — say <strong>$64.84 on Visa</strong> — tap <strong>Edit</strong> on the Out-of-pocket field and type the amount you actually paid. That co-pay is your CRA-claimable medical expense.
            </div>
          </div>
        </div>
      )}

      {/* Report-wrong-amount modal */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Report a wrong amount</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Which field?</Label>
              <Select value={reportField} onValueChange={setReportField}>
                <SelectTrigger data-testid="ocr-feedback-field" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="out_of_pocket">Out of pocket</SelectItem>
                  <SelectItem value="service_total">Total</SelectItem>
                  <SelectItem value="service_covered">Covered by insurance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Correct amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={reportCorrect}
                onChange={(e) => setReportCorrect(e.target.value)}
                placeholder="e.g. 64.84"
                data-testid="ocr-feedback-correct"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Note (optional)</Label>
              <Textarea
                rows={2}
                value={reportNote}
                onChange={(e) => setReportNote(e.target.value)}
                placeholder="Anything else worth knowing?"
                data-testid="ocr-feedback-note"
                className="mt-2"
              />
            </div>
            <div className="text-xs text-[#5C6660]">
              We&apos;ll save your correction to the form and use the report to make scans smarter for everyone.
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReportOpen(false)}
              data-testid="ocr-feedback-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitReport}
              disabled={reportBusy || !reportCorrect}
              data-testid="ocr-feedback-submit"
              className="bg-[#2C4C3B] hover:bg-[#3A634D] text-white"
            >
              {reportBusy ? "Sending…" : "Submit correction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ReceiptDropzone({ fileRef, uploading, onPick, files, onRemove, accent = "travel" }) {
  const cameraRef = useRef(null);
  const cls = accent === "rx"
    ? "border-[#D4A373]/40 bg-[#D4A373]/5 hover:bg-[#D4A373]/15"
    : "border-[#2C4C3B]/30 bg-[#2C4C3B]/5 hover:bg-[#2C4C3B]/10";
  const iconColor = accent === "rx" ? "text-[#D4A373]" : "text-[#2C4C3B]";
  return (
    <section>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent === "rx" ? "bg-[#D4A373]/15" : "bg-[#2C4C3B]/10"}`}>
          <MagicWand weight="duotone" size={18} className={iconColor} />
        </div>
        <div>
          <div className="font-[Manrope] font-semibold text-base">Receipt — auto-fill with AI</div>
          <div className="text-xs text-[#5C6660] flex items-center gap-1"><Sparkle size={12} weight="duotone" /> Snap a receipt, AI reads it</div>
        </div>
      </div>
      {/* Camera input — opens rear camera DIRECTLY on mobile via capture attr */}
      <input
        ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="trip-receipt-camera"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
      />
      {/* File input — gallery / PDF / cloud */}
      <input
        ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
        data-testid="trip-receipt-file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
      />
      {uploading ? (
        <div className={`w-full border-2 border-dashed rounded-2xl p-5 flex flex-col items-center justify-center text-center ${cls}`}>
          <Sparkle size={22} weight="duotone" className={`${iconColor} animate-pulse`} />
          <div className="mt-2 text-sm font-medium">Scanning receipt with AI…</div>
          <div className="text-xs text-[#5C6660] mt-0.5">Hang tight, this takes a few seconds</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button" onClick={() => cameraRef.current?.click()}
            data-testid="trip-receipt-snap"
            className={`border-2 border-dashed transition-colors rounded-2xl py-5 px-3 flex flex-col items-center justify-center text-center ${cls}`}
          >
            <Camera size={26} weight="duotone" className={iconColor} />
            <div className="mt-2 text-sm font-semibold">Snap a receipt</div>
            <div className="text-[11px] text-[#5C6660] mt-0.5">Opens camera</div>
          </button>
          <button
            type="button" onClick={() => fileRef.current?.click()}
            data-testid="trip-receipt-upload"
            className={`border-2 border-dashed transition-colors rounded-2xl py-5 px-3 flex flex-col items-center justify-center text-center ${cls}`}
          >
            <UploadSimple size={26} weight="duotone" className={iconColor} />
            <div className="mt-2 text-sm font-semibold">Upload</div>
            <div className="text-[11px] text-[#5C6660] mt-0.5">Photo / PDF / files</div>
          </button>
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((r) => (
            <li key={r.file_id} className="flex items-center gap-3 p-2.5 rounded-xl border border-[#E3E1D9] bg-[#EBE9E0]/40">
              <FileImage size={18} weight="duotone" className={iconColor} />
              <span className="flex-1 text-sm truncate">{r.original_filename}</span>
              <button onClick={() => onRemove(r.file_id)} className="text-[#5C6660] hover:text-[#1E2420]" data-testid={`receipt-remove-${r.file_id}`}>
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


/**
 * LetterDropzone — appointment letter uploader with AI auto-fill.
 *
 * Visually distinct from ReceiptDropzone (blue accent + envelope icon) so users
 * understand this is for the doctor's letter, not the parking/gas receipt.
 * Used by TripFormDialog to back up CRA Line 33099 travel claims with the
 * mandatory "letter from a medical practitioner" certifying the appointment.
 */
function LetterDropzone({ fileRef, uploading, onPick, files, onRemove }) {
  const cameraRef = useRef(null);
  const cls = "border-[#3D6FA8]/40 bg-[#3D6FA8]/5 hover:bg-[#3D6FA8]/15";
  const iconColor = "text-[#3D6FA8]";
  return (
    <section className="mb-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#3D6FA8]/10">
          <Envelope weight="duotone" size={18} className={iconColor} />
        </div>
        <div>
          <div className="font-[Manrope] font-semibold text-base">Appointment letter or paid statement</div>
          <div className="text-xs text-[#5C6660] flex items-center gap-1"><Sparkle size={12} weight="duotone" /> Snap it — AI fills Date, Clinic, Doctor, Purpose <em className="not-italic">+ amounts if shown</em></div>
        </div>
      </div>
      <input
        ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="trip-letter-camera"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
      />
      <input
        ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
        data-testid="trip-letter-file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
      />
      {uploading ? (
        <div className={`w-full border-2 border-dashed rounded-2xl p-5 flex flex-col items-center justify-center text-center ${cls}`}>
          <Sparkle size={22} weight="duotone" className={`${iconColor} animate-pulse`} />
          <div className="mt-2 text-sm font-medium">Reading appointment letter with AI…</div>
          <div className="text-xs text-[#5C6660] mt-0.5">Hang tight, this takes a few seconds</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button" onClick={() => cameraRef.current?.click()}
            data-testid="trip-letter-snap"
            className={`border-2 border-dashed transition-colors rounded-2xl py-5 px-3 flex flex-col items-center justify-center text-center ${cls}`}
          >
            <Camera size={26} weight="duotone" className={iconColor} />
            <div className="mt-2 text-sm font-semibold">Snap the letter</div>
            <div className="text-[11px] text-[#5C6660] mt-0.5">Opens camera</div>
          </button>
          <button
            type="button" onClick={() => fileRef.current?.click()}
            data-testid="trip-letter-upload"
            className={`border-2 border-dashed transition-colors rounded-2xl py-5 px-3 flex flex-col items-center justify-center text-center ${cls}`}
          >
            <UploadSimple size={26} weight="duotone" className={iconColor} />
            <div className="mt-2 text-sm font-semibold">Upload</div>
            <div className="text-[11px] text-[#5C6660] mt-0.5">Photo / PDF / files</div>
          </button>
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-3 space-y-2">
          {files.map((r) => (
            <li key={r.file_id} className="flex items-center gap-3 p-2.5 rounded-xl border border-[#E3E1D9] bg-[#EBE9E0]/40">
              <Envelope size={18} weight="duotone" className={iconColor} />
              <span className="flex-1 text-sm truncate">{r.original_filename}</span>
              <button onClick={() => onRemove(r.file_id)} className="text-[#5C6660] hover:text-[#1E2420]" data-testid={`letter-remove-${r.file_id}`}>
                <X size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}



/**
 * DocumentDropzone — single unified "Snap a document" CTA.
 *
 * Replaces the dual ReceiptDropzone + LetterDropzone primary flow. Calls
 * the /api/documents/scan endpoint which auto-routes between the receipt
 * and letter scanners, so the user just snaps "whatever the doctor or
 * pharmacist handed them" and MedEasy figures out what kind of doc it is.
 *
 * Renders a combined file list with a small badge showing which OCR
 * pipeline handled each upload, in case the user wants to know.
 */
function DocumentDropzone({ uploading, onPick, files, onRemove, onToggleAdvanced, advancedOpen }) {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const cls = "border-[#2C4C3B]/30 bg-[#2C4C3B]/5 hover:bg-[#2C4C3B]/10";
  const iconColor = "text-[#2C4C3B]";
  return (
    <section data-testid="unified-document-dropzone">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#2C4C3B]/10 shrink-0 mt-0.5">
          <MagicWand weight="duotone" size={20} className={iconColor} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-[Manrope] font-bold text-lg leading-snug">Snap a document — AI sorts it</div>
          <div className="text-xs text-[#5C6660] mt-0.5 flex items-center gap-1 leading-relaxed">
            <Sparkle size={12} weight="duotone" />
            Receipt, prescription, appointment letter, dental statement — we&apos;ll route it correctly and pre-fill the form.
          </div>
        </div>
      </div>
      <input
        ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="trip-doc-camera"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
      />
      <input
        ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
        data-testid="trip-doc-file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ""; }}
      />
      {uploading ? (
        <div className={`w-full border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center text-center ${cls}`}>
          <Sparkle size={26} weight="duotone" className={`${iconColor} animate-pulse`} />
          <div className="mt-2 text-sm font-semibold">Reading document with AI…</div>
          <div className="text-xs text-[#5C6660] mt-0.5">Hang tight — picking the right scanner</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button" onClick={() => cameraRef.current?.click()}
            data-testid="trip-doc-snap"
            className={`border-2 border-dashed transition-colors rounded-2xl py-6 px-3 flex flex-col items-center justify-center text-center ${cls}`}
          >
            <Camera size={28} weight="duotone" className={iconColor} />
            <div className="mt-2 text-sm font-semibold">Snap a document</div>
            <div className="text-[11px] text-[#5C6660] mt-0.5">Opens camera</div>
          </button>
          <button
            type="button" onClick={() => fileRef.current?.click()}
            data-testid="trip-doc-upload"
            className={`border-2 border-dashed transition-colors rounded-2xl py-6 px-3 flex flex-col items-center justify-center text-center ${cls}`}
          >
            <UploadSimple size={28} weight="duotone" className={iconColor} />
            <div className="mt-2 text-sm font-semibold">Upload</div>
            <div className="text-[11px] text-[#5C6660] mt-0.5">Photo / PDF / files</div>
          </button>
        </div>
      )}
      {files.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid="trip-doc-list">
          {files.map((r) => {
            const isLetter = r._routed_as === "letter";
            const Icon = isLetter ? Envelope : FileImage;
            const accent = isLetter ? "text-[#3D6FA8]" : "text-[#2C4C3B]";
            const badge = isLetter ? "Letter / statement" : "Receipt";
            return (
              <li key={r.file_id} className="flex items-center gap-3 p-2.5 rounded-xl border border-[#E3E1D9] bg-[#EBE9E0]/40">
                <Icon size={18} weight="duotone" className={accent} />
                <span className="flex-1 text-sm truncate">{r.original_filename}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#5C6660] font-semibold whitespace-nowrap">{badge}</span>
                <button
                  onClick={() => onRemove(r.file_id, r._routed_as)}
                  className="text-[#5C6660] hover:text-[#1E2420]"
                  data-testid={`doc-remove-${r.file_id}`}
                  aria-label="Remove document"
                >
                  <X size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {onToggleAdvanced && (
        <button
          type="button"
          onClick={onToggleAdvanced}
          data-testid="trip-doc-advanced-toggle"
          className="mt-3 text-xs text-[#5C6660] hover:text-[#2C4C3B] underline-offset-2 hover:underline"
        >
          {advancedOpen ? "Hide advanced upload" : "Need to force-route a doc? Show advanced upload"}
        </button>
      )}
    </section>
  );
}
